import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { listPatientRelayOrdersApi } from 'api/relay';
import { handleError } from 'store/thunks/errorHandlerThunks';

const FIRST_PAGE = 1;

const requestKey = (mpi, page, attempt) => `${mpi}:${page}:${attempt}`;

const firstRequest = (mpi) => ({ mpi, page: FIRST_PAGE, attempt: 0 });

/**
 * POM-6279 — one patient's relay orders for the patient page's Relay Orders tab.
 *
 * LOCAL state, deliberately not the relayQueue slice: the Central Fill queue owns that slice, and a
 * patient page writing its rows there would show one patient's orders in the queue (and the
 * reverse) the next time either is opened.
 *
 * Every result is stored with the patient and page it answers, and only returned while that is
 * still what is on screen. That one rule does three jobs:
 * - a slow response for patient A that lands after the page moved to patient B is never shown
 *   (the `cancelled` guard also stops it being stored or reported at all);
 * - patient A's rows are not shown for even one render under patient B -- B reads as loading
 *   from the render that changed the patient, not from the effect after it;
 * - the page resets to 1 for a new patient without first asking for B's copy of A's page.
 *
 * The request (patient, page, retry attempt) is RESET whenever the patient changes, so returning
 * to a patient starts at page 1 again rather than on the page last viewed there. The reset is
 * applied while rendering, React's pattern for adjusting state to a prop, so the stale page never
 * reaches a request.
 *
 * `retry` re-asks for the same page. The attempt is part of the result key, so a retry reads as
 * loading at once instead of holding the failure on screen until the answer lands.
 *
 * Fetches only while the tab is active (patient tab panels are always mounted, just hidden), and
 * again on every activation, as the other patient tabs do.
 *
 * A failed read is reported through handleError and settles as `failed` with no rows, so the tab
 * never sits on a loading state for a read that is not coming back.
 */
const usePatientRelayOrders = ({ mpi, isActive }) => {
  const dispatch = useDispatch();
  const [request, setRequest] = useState(() => firstRequest(mpi));
  const [result, setResult] = useState(null);

  if (request.mpi !== mpi) {
    setRequest(firstRequest(mpi));
  }

  const { page, attempt } = request.mpi === mpi ? request : firstRequest(mpi);
  const key = requestKey(mpi, page, attempt);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    let cancelled = false;

    listPatientRelayOrdersApi({ patientId: mpi, page })
      .then(({ items, totalCount }) => {
        if (!cancelled) {
          setResult({ key, mpi, rows: items, count: totalCount, failed: false });
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setResult({ key, mpi, rows: [], count: 0, failed: true });
        dispatch(handleError({ error }));
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isActive, key, mpi, page]);

  const onPageChange = useCallback(
    (nextPage) => setRequest((current) => ({ ...current, page: nextPage })),
    []
  );

  const retry = useCallback(
    () => setRequest((current) => ({ ...current, attempt: current.attempt + 1 })),
    []
  );

  const isCurrent = result?.key === key;
  // The count survives a page change for the same patient, so the pagination does not vanish
  // while the next page loads. It never survives a change of patient.
  // `result !== null` first: with no mpi, `result?.mpi === mpi` is undefined === undefined.
  const isSamePatient = result !== null && result.mpi === mpi;

  return {
    rows: isCurrent ? result.rows : undefined,
    count: isSamePatient ? result.count : 0,
    page,
    onPageChange,
    retry,
    failed: isCurrent && result.failed,
  };
};

export default usePatientRelayOrders;
